/**
 * appEventBus.js
 *
 * Lightweight global event bus for decoupled cross-feature communication.
 *
 * Usage:
 *   import AppEvents from '../utils/appEventBus';
 *
 *   // Subscribe
 *   const unsub = AppEvents.on('DocumentCreated', (payload) => { ... });
 *   // Remember to call unsub() in useEffect cleanup.
 *
 *   // Emit
 *   AppEvents.emit('VariantCreated', { id: 'var_123', name: 'JD Tailored' });
 *
 * Event Catalog:
 *   DocumentCreated   { id, type, name }
 *   DocumentUpdated   { id, type, fields }
 *   DocumentDeleted   { id, type }
 *   TemplateChanged   { templateId, variantId }
 *   VariantCreated    { id, name }
 *   ImportFinished    { id, fileName, sections }
 *   ImportFailed      { fileName, error }
 *   ImportEdit        { mode }           — user chose "Edit before importing"
 *   AutosaveFinished  { id, type }
 *   AutosaveFailed    { id, type, error }
 *   ExportFinished    { id, format }
 *   ExportFailed      { id, format, error }
 *   NetworkOnline     {}
 *   NetworkOffline    {}
 *   WorkspaceReady    { id, type }
 *   ResumeDataChanged { path, value }    — field updated in resume editor/AI
 *   VaultDataChanged  { field, value }   — field updated in Career Vault
 */

class EventBus {
  constructor() {
    this._listeners = {};
  }

  /**
   * Subscribe to an event.
   * @param {string} event - Event name.
   * @param {Function} handler - Callback receives the emitted payload.
   * @returns {Function} Unsubscribe function.
   */
  on(event, handler) {
    if (!this._listeners[event]) {
      this._listeners[event] = [];
    }
    this._listeners[event].push(handler);

    // Return unsubscribe function for useEffect cleanup
    return () => {
      this._listeners[event] = (this._listeners[event] || []).filter(h => h !== handler);
    };
  }

  /**
   * Emit an event to all subscribers.
   * @param {string} event - Event name.
   * @param {Object} payload - Data passed to all handlers.
   */
  emit(event, payload = {}) {
    const handlers = this._listeners[event] || [];
    handlers.forEach(h => {
      try {
        h(payload);
      } catch (err) {
        console.error(`[AppEventBus] Error in handler for "${event}":`, err);
      }
    });
  }

  /**
   * Subscribe to an event exactly once.
   */
  once(event, handler) {
    const unsub = this.on(event, (payload) => {
      handler(payload);
      unsub();
    });
    return unsub;
  }
}

const AppEvents = new EventBus();
export default AppEvents;
